import type { PricingOptionItem } from "./types";
import {
  MemberProduct,
  MemberProductLevelEnum,
} from "@/src/features/api/memberApi";

export type SupportedMobilePricingPlanType =
  | "monthly"
  | "yearly"
  | "weekly"
  | "lifetime";
export type LegacyPurchasePlanType = Extract<
  SupportedMobilePricingPlanType,
  "monthly" | "yearly"
>;

interface SelectionUserPlanInfo {
  isPro?: boolean;
  isExpert?: boolean;
  isFree?: boolean;
  payCycle?: "MONTH" | "YEAR" | "WEEK" | "LIFETIME" | "DAY";
  product?: {
    id?: string;
    name?: string;
    renewal_id?: string;
  };
}

interface ResolveMobilePricingSelectionParams {
  planIdParam?: string | null;
  data?: MemberProduct[];
  userPlanInfo?: SelectionUserPlanInfo;
}

export const DEFAULT_PLAN_ORDER: SupportedMobilePricingPlanType[] = [
  "weekly",
  "monthly",
  "yearly",
  "lifetime",
];

const mapPayCycleToPlanType = (
  payCycle?: MemberProduct["pay_cycle"],
): SupportedMobilePricingPlanType | null => {
  if (payCycle === "MONTH") {
    return "monthly";
  }
  if (payCycle === "YEAR") {
    return "yearly";
  }
  if (payCycle === "WEEK") {
    return "weekly";
  }
  if (payCycle === "LIFETIME") {
    return "lifetime";
  }
  return null;
};

export function getDefaultPlanType(
  userPlanInfo?: SelectionUserPlanInfo,
): SupportedMobilePricingPlanType {
  if (userPlanInfo?.payCycle) {
    const mapped = mapPayCycleToPlanType(userPlanInfo.payCycle);
    if (mapped) {
      return mapped;
    }
  }
  return "yearly";
}

export function resolveSelectionByPlanId({
  planIdParam,
  data,
}: {
  planIdParam?: string | null;
  data?: MemberProduct[];
}): { selectedPlanType: SupportedMobilePricingPlanType } | null {
  if (!planIdParam || !data?.length) {
    return null;
  }

  const matchedPlan = data.find((plan) => plan.id === planIdParam);
  if (!matchedPlan) {
    return null;
  }

  const planType = mapPayCycleToPlanType(matchedPlan.pay_cycle);
  if (!planType || matchedPlan.level === MemberProductLevelEnum.FREE) {
    return null;
  }

  return { selectedPlanType: planType };
}

export function resolveMobilePricingSelection({
  planIdParam,
  data,
  userPlanInfo,
}: ResolveMobilePricingSelectionParams) {
  const routeSelection = resolveSelectionByPlanId({ planIdParam, data });
  if (routeSelection) {
    return routeSelection;
  }

  return {
    selectedPlanType: getDefaultPlanType(userPlanInfo),
  };
}

export function orderPricingOptions(
  pricingOptions: PricingOptionItem[],
): PricingOptionItem[] {
  const optionByKey = new Map(
    pricingOptions.map((option) => [option.key, option]),
  );

  return DEFAULT_PLAN_ORDER.flatMap((key) => {
    const option = optionByKey.get(key);
    return option ? [option] : [];
  });
}

interface ResolvePricingOptionsDisplayParams {
  pricingOptions: PricingOptionItem[];
  userPlanInfo?: SelectionUserPlanInfo;
  isExpanded: boolean;
}

export function resolvePricingOptionsDisplay({
  pricingOptions,
  userPlanInfo,
  isExpanded,
}: ResolvePricingOptionsDisplayParams) {
  const orderedPricingOptions = orderPricingOptions(pricingOptions);
  const defaultPlanType = getDefaultPlanType(userPlanInfo);
  const collapsedPricingOptions = orderedPricingOptions.filter(
    (option) => option.key === defaultPlanType,
  );
  const hasMultiplePlans = orderedPricingOptions.length > 1;

  return {
    orderedPricingOptions,
    displayedPricingOptions: isExpanded
      ? orderedPricingOptions
      : collapsedPricingOptions,
    shouldShowMorePlans: !isExpanded && hasMultiplePlans,
    shouldShowLessPlans: isExpanded && hasMultiplePlans,
  };
}
